import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Close';
import BiztantuDragTable from '../../../Components/BiztantuDragTable';
import TextFiledWithLabel from '../../../Components/TextFieldWithLabel';
import { handleValidateCodevalue } from '../../../Utility/constant';
import useI18nSync from '../../../Utility/hooks/useI18nSync';
import tableBodyicon from '../../../Assets/images/tablebody_edit.png';
import tableEditicon from '../../../Assets/images/tableheader_edit.png';
import trashIcon from '../../../Assets/images/trashIcon.png';
import disabledEdit from '../../../Assets/images/disabled_edit.png';
import disabledDeleteIcon from '../../../Assets/images/disabledTrashIcon.png';
import './ClientLookupTableV2.css';

const ClientLookupTableV2 = ({
  clientName,
  lookupdetails,
  enqueueSnackbar,
  handleDeleteclientLookup,
  handleUpdateClinetlookup,
  handleReorderClientLookup,
  selectedClient,
  currentUser
}) => {
  const { resolveDisplayName, syncI18nDisplayName, internationalise } = useI18nSync();

  const [rows, setRows] = useState([]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [isReorderMode, setIsReorderMode] = useState(false);

  // Sync rows from lookupdetails.clientLookup
  useEffect(() => {
    if (lookupdetails?.clientLookup) {
      const sorted = [...lookupdetails.clientLookup]
        .sort((a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0))
        .map((item) => ({
          ...item,
          systemLookupCodeName: item?.systemLookupCode?.name || ""
        }));
      setRows(sorted);
      setEditingRowId(null);
      setEditFormData({});
    }
  }, [lookupdetails?.clientLookup]);

  const isAnyRowEditing = Boolean(editingRowId);

  // Add new client lookup row
  const handleAddClick = useCallback(() => {
    if (!selectedClient) {
      enqueueSnackbar(
        handleValidateCodevalue(internationalise, "LU_SELECT_A_CLIENT_WARNING"),
        { variant: "warning" }
      );
      return;
    }

    const newId = `new-${Date.now()}`;
    const newRow = {
      id: newId,
      name: "",
      systemLookupCodeName: "",
      sequence: rows.length + 1,
      status: "ACTIVE",
      isNew: true
    };

    setRows((prev) => [newRow, ...prev.filter((r) => !r.isNew)]);
    setEditingRowId(newId);
    setEditFormData({
      id: newId,
      name: "",
      systemLookupCodeName: "",
      displayName: "",
      originalName: "",
      sequence: rows.length + 1,
      status: "ACTIVE",
      isNew: true
    });
  }, [selectedClient, rows.length, enqueueSnackbar, internationalise]);

  // Start inline editing
  const handleEditClick = useCallback((row) => {
    setEditingRowId(row.id);
    setEditFormData({
      id: row.id,
      name: row.name || "",
      systemLookupCodeName: row.systemLookupCodeName || row.systemLookupCode?.name || "",
      displayName: resolveDisplayName(row.name),
      originalName: row.name || "",
      sequence: row.sequence,
      status: row.status,
      isNew: row.isNew || false
    });
  }, [resolveDisplayName]);

  // Cancel inline editing
  const handleCancelClick = useCallback((id) => {
    setRows((prev) => {
      const targetRow = prev.find((r) => r.id === id);
      return targetRow?.isNew ? prev.filter((r) => r.id !== id) : prev;
    });
    setEditingRowId(null);
    setEditFormData({});
  }, []);

  // Save inline edit (saves lookup code + syncs i18n display name)
  const handleSaveRowClick = useCallback(async (id) => {
    if (!editFormData.name || !editFormData.name.trim()) {
      enqueueSnackbar(handleValidateCodevalue(internationalise, "LU_ENTER_SLU_WARNING"), { variant: "warning" });
      return;
    }
    if (!editFormData.systemLookupCodeName) {
      enqueueSnackbar(handleValidateCodevalue(internationalise, "LU_ENTER_LU_WARNING"), { variant: "warning" });
      return;
    }

    const trimmedName = editFormData.name.trim();
    const trimmedDisplayName = (editFormData.displayName || "").trim();
    const originalName = editFormData.originalName;
    const clientId = currentUser?.client?.id || selectedClient?.id;

    const payload = {
      ...editFormData,
      name: trimmedName,
      sequence: editFormData.sequence !== undefined ? editFormData.sequence : rows.length
    };

    // 1. Update/save the lookup code
    const result = await handleUpdateClinetlookup(payload, selectedClient);

    // Only close edit mode and sync i18n if save was successful
    if (result?.success) {
      setEditingRowId(null);
      setEditFormData({});

      // 2. Sync i18n Display Name via reusable hook
      if (trimmedDisplayName && clientId) {
        await syncI18nDisplayName({
          clientId,
          code: trimmedName,
          originalCode: originalName,
          value: trimmedDisplayName
        });
      }
    }
  }, [
    editFormData,
    enqueueSnackbar,
    internationalise,
    handleUpdateClinetlookup,
    selectedClient,
    currentUser,
    rows.length,
    syncI18nDisplayName
  ]);

  // Columns definition for BiztantuDragTable
  const columns = useMemo(() => [
    {
      field: 'actions',
      headerName: (
        <Tooltip title={handleValidateCodevalue(internationalise, 'COMMON_ACTION')}>
          <img src={tableEditicon} alt="actions" style={{ height: '18px', verticalAlign: 'middle' }} />
        </Tooltip>
      ),
      width: 90,
      align: 'center',
      sortable: false,
      renderCell: ({ row }) => {
        const isCurrentRowEditing = editingRowId === row.id;

        if (isCurrentRowEditing) {
          return (
            <Stack direction="row" spacing={0.5} justifyContent="center" className="lookup-actions-stack">
              <Tooltip title={handleValidateCodevalue(internationalise, 'COMMON_SAVE')}>
                <IconButton
                  size="small"
                  onClick={() => handleSaveRowClick(row.id)}
                  className="textPrimary"
                >
                  <CheckIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={handleValidateCodevalue(internationalise, 'COMMON_CANCEL')}>
                <IconButton
                  size="small"
                  onClick={() => handleCancelClick(row.id)}
                  className="textPrimary"
                  color="inherit"
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        }

        return (
          <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center" className="lookup-actions-stack">
            <Tooltip title={handleValidateCodevalue(internationalise, 'COMMON_EDIT')}>
              <span>
                <IconButton
                  size="small"
                  disabled={isAnyRowEditing || isReorderMode || row.status === 'INACTIVE'}
                  onClick={() => handleEditClick(row)}
                >
                  <img
                    src={isAnyRowEditing || isReorderMode || row.status === 'INACTIVE' ? disabledEdit : tableBodyicon}
                    alt="edit"
                    style={{ height: '18px' }}
                  />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title={handleValidateCodevalue(internationalise, 'COMMON_DELETE')}>
              <span>
                <IconButton
                  size="small"
                  disabled={isAnyRowEditing || isReorderMode}
                  onClick={() => handleDeleteclientLookup(row.id)}
                >
                  <img
                    src={isAnyRowEditing || isReorderMode ? disabledDeleteIcon : trashIcon}
                    alt="delete"
                    style={{ height: '18px' }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      }
    },
    {
      field: 'systemLookupCode.name',
      headerName: handleValidateCodevalue(internationalise, 'CONF_LU_EDIT_SLC'),
      width: '28%',
      minWidth: 220,
      editMinWidth: 260,
      sortable: true,
      renderCell: ({ row }) => {
        const isCurrentRowEditing = editingRowId === row.id;

        if (isCurrentRowEditing && row.isNew) {
          return (
            <TextField
              select
              fullWidth
              size="small"
              autoFocus
              value={editFormData.systemLookupCodeName || ""}
              onChange={(e) =>
                setEditFormData((prev) => ({
                  ...prev,
                  systemLookupCodeName: e.target.value
                }))
              }
              className="lookup-full-input"
            >
              {(lookupdetails?.systemlookup || []).map((option) => (
                <MenuItem key={option.name} value={option.name}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          );
        }

        return (
          <Typography variant="body2" className="lookup-cell-text">
            {row.systemLookupCode?.name || row.systemLookupCodeName || "-"}
          </Typography>
        );
      }
    },
    {
      field: 'name',
      headerName: handleValidateCodevalue(internationalise, 'CONF_LU_EDIT_NAME'),
      width: '36%',
      minWidth: 260,
      editMinWidth: 320,
      sortable: true,
      renderCell: ({ row }) => {
        const isCurrentRowEditing = editingRowId === row.id;

        if (isCurrentRowEditing) {
          return (
            <TextField
              fullWidth
              size="small"
              autoFocus={!row.isNew}
              value={editFormData.name || ""}
              onChange={(e) =>
                setEditFormData((prev) => ({
                  ...prev,
                  name: e.target.value
                }))
              }
              placeholder={handleValidateCodevalue(internationalise, 'CONF_LU_EDIT_NAME')}
              className="lookup-full-input"
            />
          );
        }

        return (
          <Typography variant="body2" className="lookup-cell-text">
            {row.name || "-"}
          </Typography>
        );
      }
    },
    {
      field: 'displayName',
      headerName: handleValidateCodevalue(internationalise, 'CONF_LU_I18_VALUE'),
      width: '28%',
      minWidth: 240,
      editMinWidth: 280,
      sortable: true,
      renderCell: ({ row }) => {
        const isCurrentRowEditing = editingRowId === row.id;

        if (isCurrentRowEditing) {
          return (
            <TextField
              fullWidth
              size="small"
              value={editFormData.displayName || ""}
              onChange={(e) =>
                setEditFormData((prev) => ({
                  ...prev,
                  displayName: e.target.value
                }))
              }
              placeholder={handleValidateCodevalue(internationalise, 'CONF_I18_SEARCH_VALUE')}
              className="lookup-full-input"
            />
          );
        }

        const dispValue = resolveDisplayName(row.name);

        return (
          <Typography
            variant="body2"
            className={dispValue ? 'lookup-cell-text' : 'lookup-cell-text-muted'}
          >
            {dispValue || '-'}
          </Typography>
        );
      }
    },
    {
      field: 'status',
      headerName: handleValidateCodevalue(internationalise, 'LU_STATUS'),
      width: 100,
      align: 'center',
      sortable: true,
      renderCell: ({ row }) => (
        <Typography variant="body2" className="lookup-status-text">
          {row.status || "ACTIVE"}
        </Typography>
      )
    }
  ], [
    internationalise,
    editingRowId,
    isAnyRowEditing,
    editFormData,
    lookupdetails?.systemlookup,
    resolveDisplayName,
    handleSaveRowClick,
    handleCancelClick,
    handleEditClick,
    handleDeleteclientLookup,
    isReorderMode
  ]);

  return (
    <div className="client-lookup-table-v2">
      <BiztantuDragTable
        columns={columns}
        rows={rows}
        rowKey="id"
        sequenceField="sequence"
        isReorderable={true}
        onConfirmReorder={handleReorderClientLookup}
        onAddClick={handleAddClick}
        addBtnLabel={handleValidateCodevalue(internationalise, "TA_ADD_CLIENT_LU")}
        isAddDisabled={!selectedClient}
        isAnyRowEditing={isAnyRowEditing}
        editingRowId={editingRowId}
        reorderBtnLabel={handleValidateCodevalue(internationalise, 'CONF_LU_EDIT_ORDER')}
        confirmBtnLabel={handleValidateCodevalue(internationalise, 'COMMON_SAVE')}
        cancelBtnLabel={handleValidateCodevalue(internationalise, 'COMMON_CANCEL')}
        reorderInfoKey="CONF_LU_REORDER_INFO"
        onReorderModeChange={setIsReorderMode}
        actionBarLeft={
          <TextFiledWithLabel
            label={handleValidateCodevalue(internationalise, 'CONF_LU_EDIT_SELECT_CLIENT')}
            value={clientName}
            sx={{ minWidth: "40%", margin: "0px 10px" }}
            disabled
          />
        }
      />
    </div>
  );
};

export default React.memo(ClientLookupTableV2);
